/**
 * Zod schemas for the Refine admin flagged-events review queue (WEB-452).
 *
 * Backs `GET /api/v1/admin/flagged-events` — the human-in-the-loop triage list
 * that makes the data-quality quarantine policy reversible in practice.
 *
 * ## Why this is `qualityStatus`-driven, not `FlaggedEvent`-driven
 *
 * The queue is sourced from the `events` table by `qualityStatus`, NOT from
 * `FlaggedEvent` rows. Automated scoring (`persistScoredEvent` /
 * `qualityScoreBackfill` in data-pipeline) writes `Event.qualityStatus =
 * 'QUARANTINED'` directly and never creates a `FlaggedEvent`, so the
 * vast majority of quarantines never produced a flag row — the queue read off
 * `flagged_events` was therefore structurally empty in production even though
 * hundreds of events were held back. The route now derives the queue from the
 * live `qualityStatus` verdict (the same column the admin events list filters
 * on) and LEFT JOINs any explicit `FlaggedEvent` metadata, so it reflects the
 * real remediation backlog with a single source of truth.
 *
 * Defaults resolve to the quarantine surface (`qualityStatus = 'QUARANTINED'`):
 * the bare list call (no query params — the only shape the Refine dataProvider
 * sends for this resource) returns exactly the events held out of public
 * surfaces that an editor needs to enrich. `qualityStatus = 'all'` widens to
 * the full unhealthy backlog (`QUARANTINED` + `LOW_CONFIDENCE`).
 *
 * Error envelope: the route reshapes Zod failures into the canonical
 * `{ error, code, message }` envelope (NOT the `@loci/validation`
 * `createErrorResponse` shape, which the Refine client does not expect).
 */
import { z } from 'zod';

/** Default page size when Refine doesn't specify one. */
export const ADMIN_FLAGGED_EVENTS_LIST_DEFAULT_PAGE_SIZE = 20;
/** Hard cap mirroring the events list bound. */
export const ADMIN_FLAGGED_EVENTS_LIST_MAX_PAGE_SIZE = 200;

/**
 * Allow-listed `reviewStatus` values an operator may filter by. Mirrors the
 * documented status set on the Prisma `FlaggedEvent` model
 * (`pending | approved | rejected | merged`) plus `resolved`, which API-1508
 * writes when a quarantine override is cleared.
 *
 * Retained for backward compatibility — see `reviewStatus` on the query schema.
 */
export const adminFlaggedEventReviewStatusSchema = z.enum([
  'pending',
  'approved',
  'rejected',
  'merged',
  'resolved',
]);
export type AdminFlaggedEventReviewStatus = z.infer<typeof adminFlaggedEventReviewStatusSchema>;

/**
 * Quality-status bands an operator may scope the queue to. Mirrors the
 * canonical `QualityStatus` union in `@loci/types`. `QUARANTINED` (default) is
 * the held-out-of-public surface; `LOW_CONFIDENCE` is published-but-weak. The
 * literal `all` widens the queue to the full unhealthy backlog
 * (`QUARANTINED` + `LOW_CONFIDENCE`).
 */
export const adminFlaggedEventQualityStatusSchema = z.enum([
  'PREMIUM',
  'STANDARD',
  'LOW_CONFIDENCE',
  'QUARANTINED',
]);
export type AdminFlaggedEventQualityStatus = z.infer<typeof adminFlaggedEventQualityStatusSchema>;

/**
 * Review-queue tab taxonomy (API-1579). Each value maps to a REAL column
 * predicate over `Event.qualityReasons` / `FlaggedEvent.flagCategory` /
 * `FlaggedEvent.reviewStatus` — see the route's `buildFilterPredicate` for the
 * authoritative SQL mapping. `all` (the default) applies no extra filter beyond
 * the base queue predicate.
 *
 * Honest-zero contract: a value whose backing column has no rows today (e.g.
 * `escalated`, since no writer sets `FlaggedEvent.reviewStatus = 'escalated'`)
 * returns a real ZERO count and empty `data` — it is never fabricated. The
 * value is still accepted so the predicate is wired the day a writer exists.
 */
export const adminFlaggedEventFilterSchema = z.enum([
  'all',
  'spam',
  'dup',
  'reports',
  'new',
  'escalated',
  /** Pending `FlaggedEvent.flagCategory = 'low-confidence-category'` (WEB-608). */
  'category',
]);
export type AdminFlaggedEventFilter = z.infer<typeof adminFlaggedEventFilterSchema>;

/**
 * Review-queue sort taxonomy (API-1579).
 * - `oldest` (DEFAULT) — `COALESCE(flaggedAt, qualityScoredAt, updatedAt) ASC`,
 *   the longest-waiting remediation targets first.
 * - `score` — `qualityScore ASC NULLS LAST`, the lowest-quality events first.
 * - `reports` — user-reported flags first (`flagCategory = 'user-reported'`),
 *   then oldest-first as a stable tiebreaker. `flagged_events.eventId` is
 *   `@unique`, so there is at most one report per event (no multi-report
 *   counter exists to aggregate); this surfaces the reported events at the top.
 */
export const adminFlaggedEventSortSchema = z.enum(['oldest', 'score', 'reports']);
export type AdminFlaggedEventSort = z.infer<typeof adminFlaggedEventSortSchema>;

/** Filter-tab counts returned inline so the FE renders tabs without a 2nd round-trip. */
export interface AdminFlaggedEventCounts {
  all: number;
  spam: number;
  dup: number;
  reports: number;
  new: number;
  escalated: number;
  /** Pending low-confidence-category flags (WEB-608). */
  category: number;
}

/**
 * Query schema for `GET /api/v1/admin/flagged-events`.
 *
 * `qualityStatus` defaults to `QUARANTINED` (the quarantine surface). Pass a
 * specific band to scope, or the literal `all` to widen to the full unhealthy
 * backlog. Pending `FlaggedEvent` rows (duplicate-candidate, user-reported,
 * geocoding-failed) are ALWAYS surfaced regardless of `qualityStatus`, so the
 * route keeps its contract as the catch-all review queue.
 *
 * `reviewStatus` / `flagCategory` are retained as optional, accepted-but-ignored
 * fields purely so a stale client that still sends them does not get a 400 from
 * the `.strict()` parser. The sole live caller (the Refine `flagged-events`
 * resource) sends only `page` + `pageSize`.
 */
export const adminFlaggedEventsListQuerySchema = z
  .object({
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce
      .number()
      .int()
      .min(1)
      .max(ADMIN_FLAGGED_EVENTS_LIST_MAX_PAGE_SIZE)
      .default(ADMIN_FLAGGED_EVENTS_LIST_DEFAULT_PAGE_SIZE),
    /**
     * Quality-status scope. Defaults to `QUARANTINED`. A specific band scopes
     * the quality predicate; the literal `all` widens it to the full unhealthy
     * backlog (`QUARANTINED` + `LOW_CONFIDENCE`).
     */
    qualityStatus: z
      .union([adminFlaggedEventQualityStatusSchema, z.literal('all')])
      .default('QUARANTINED'),
    /**
     * Review-queue tab filter (API-1579). Each value narrows the queue by a
     * REAL column predicate (see `adminFlaggedEventFilterSchema`). Defaults to
     * `all` (no extra predicate). An unbacked value (`escalated`) is accepted
     * and returns an honest zero — never fabricated rows.
     */
    filter: adminFlaggedEventFilterSchema.default('all'),
    /**
     * Review-queue sort order (API-1579). Defaults to `oldest`
     * (`COALESCE(flaggedAt, qualityScoredAt, updatedAt) ASC`), which is the
     * backward-compatible worst-first ordering for the longest-waiting items.
     */
    sort: adminFlaggedEventSortSchema.default('oldest'),
    /**
     * @deprecated Accepted but ignored. The queue is driven by `qualityStatus`
     * and an always-on pending-flag predicate; an explicit `reviewStatus` is no
     * longer a meaningful filter. Kept so a legacy client can't trip `.strict()`.
     */
    reviewStatus: z.union([adminFlaggedEventReviewStatusSchema, z.literal('all')]).optional(),
    /**
     * @deprecated Accepted but ignored (see `reviewStatus`). Bounded length
     * still guards against an unbounded value reaching the parser.
     */
    flagCategory: z.union([z.literal('all'), z.string().trim().min(1).max(120)]).optional(),
  })
  .strict();
export type AdminFlaggedEventsListQuery = z.infer<typeof adminFlaggedEventsListQuerySchema>;
